import React from 'react';
import { Container, Typography, Box, Grid, Paper, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import customTheme from '../../theme';

const GradientHero = styled(Box)({
  background: `linear-gradient(135deg, ${customTheme.colors.secondary[300]} 0%, ${customTheme.colors.secondary[500]} 100%)`,
  color: customTheme.colors.secondary[900],
  py: 6,
  borderRadius: customTheme.borderRadius.lg,
  mb: 4,
  textAlign: 'center',
  position: 'relative',
});

const ValueCard = styled(Paper)({
  p: 3,
  textAlign: 'center',
  borderRadius: customTheme.borderRadius.lg,
  transition: 'transform 0.3s ease',
  border: `1px solid ${customTheme.colors.secondary[100]}`,
  '&:hover': {
    transform: 'translateY(-4px)',
  },
});

export default function Team() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const teamValues = [
    {
      icon: <GroupsIcon sx={{ fontSize: 48, color: customTheme.colors.primary[600] }} />,
      title: t('team_page.values.teamwork.title'),
      description: t('team_page.values.teamwork.description'),
    },
    {
      icon: <EmojiPeopleIcon sx={{ fontSize: 48, color: customTheme.colors.primary[600] }} />,
      title: t('team_page.values.proximity.title'),
      description: t('team_page.values.proximity.description'),
    },
    {
      icon: <LightbulbIcon sx={{ fontSize: 48, color: customTheme.colors.primary[600] }} />,
      title: t('team_page.values.innovation.title'),
      description: t('team_page.values.innovation.description'),
    },
    {
      icon: <HandshakeIcon sx={{ fontSize: 48, color: customTheme.colors.primary[600] }} />,
      title: t('team_page.values.integrity.title'),
      description: t('team_page.values.integrity.description'),
    },
  ];

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <GradientHero sx={{ p: { xs: 4, md: 6 } }}>
          <IconButton
            onClick={() => navigate('/')}
            sx={{
              position: 'absolute',
              left: { xs: 8, md: 16 },
              top: { xs: 8, md: 16 },
              color: 'inherit',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h2" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
            {t('team_page.title')}
          </Typography>
          <Typography variant="h5" sx={{ maxWidth: 800, mx: 'auto', opacity: 0.85 }}>
            {t('team_page.subtitle')}
          </Typography>
        </GradientHero>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 3, color: customTheme.colors.primary[800] }}>
              {t('team_page.who_we_are')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 3 }}>
              {t('team_page.who_we_are_text_1')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
              {t('team_page.who_we_are_text_2')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
              alt={t('team_page.title')}
              sx={{
                width: '100%',
                height: 300,
                objectFit: 'cover',
                borderRadius: 2,
                boxShadow: 3,
              }}
            />
          </Grid>
        </Grid>

        <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 4, textAlign: 'center', color: customTheme.colors.primary[800] }}>
          {t('team_page.team_values')}
        </Typography>
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {teamValues.map((value, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <ValueCard elevation={2} sx={{ p: 3, height: '100%' }}>
                <Box sx={{ mb: 2 }}>{value.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {value.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                  {value.description}
                </Typography>
              </ValueCard>
            </Grid>
          ))}
        </Grid>

        <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, textAlign: 'center', mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: customTheme.colors.primary[800] }}>
            {t('team_page.join_us')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto', mb: 3, lineHeight: 1.8 }}>
            {t('team_page.join_us_text')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, color: customTheme.colors.primary[700] }}>
            {t('team_page.join_us_cta')}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
